import React, { useEffect, useState } from 'react';
import './App.css';

const API_URL = 'https://jsfrlib6a8.execute-api.us-west-1.amazonaws.com/prod/fetch-gapi-data';

function App() {
  const [data, setData] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_URL);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const result = await response.json();
        setData(result);
        const uniqueMakes = [...new Set(result.map((item) => item.make))].filter(Boolean).sort();
        setMakes(uniqueMakes);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load vehicle data. Please try refreshing the page.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleMakeChange = (e) => {
    const make = e.target.value;
    setSelectedMake(make);
    setSelectedModel('');
    setStatus(null);
    const filteredModels = data
      .filter((item) => item.make === make)
      .map((item) => item.model)
      .filter(Boolean)
      .sort();
    setModels(filteredModels);
  };

  const handleModelChange = (e) => {
    const model = e.target.value;
    setSelectedModel(model);
    const vehicle = data.find(
      (item) => item.make === selectedMake && item.model === model
    );
    setStatus(vehicle);
  };

  const handleReset = () => {
    setSelectedMake('');
    setSelectedModel('');
    setStatus(null);
    setModels([]);
  };

  return (
    <div className="App">
      <header className="header">
        <div className="header-inner">
          <img
            src={`${process.env.PUBLIC_URL}/parkworks_logo.png`}
            alt="Parkworks Logo"
            className="header-logo"
          />
          <div className="header-text">
            <h1 className="header-title">Vehicle Compatibility Checker</h1>
            <p className="header-subtitle">Check if your vehicle works with our smart garage systems</p>
          </div>
        </div>
      </header>

      <main className="container">
        {loading ? (
          <div className="loading">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading vehicle data...</p>
          </div>
        ) : error ? (
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button onClick={() => window.location.reload()}>Try Again</button>
          </div>
        ) : (
          <>
            <div className="form-section">
              <div className="form-group">
                <label htmlFor="make">
                  <i className="fas fa-car"></i> Vehicle Make
                </label>
                <select id="make" value={selectedMake} onChange={handleMakeChange}>
                  <option value="">-- Select Make --</option>
                  {makes.map((make, index) => (
                    <option key={`${make}-${index}`} value={make}>{make}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="model">
                  <i className="fas fa-car-side"></i> Vehicle Model
                </label>
                <select
                  id="model"
                  value={selectedModel}
                  onChange={handleModelChange}
                  disabled={!selectedMake}
                >
                  <option value="">{selectedMake ? '-- Select Model --' : '-- Select a make first --'}</option>
                  {models.map((model, index) => (
                    <option key={`${model}-${index}`} value={model}>{model}</option>
                  ))}
                </select>
              </div>

              {(selectedMake || selectedModel) && (
                <button className="reset-btn" onClick={handleReset}>
                  <i className="fas fa-redo"></i> Reset
                </button>
              )}
            </div>

            <div className="status-section">
              {status ? (
                <div className="status-cards">
                  <div className={`status-card ${status.garageCompatible ? 'status-green' : 'status-red'}`}>
                    <i className={`fas ${status.garageCompatible ? 'fa-check-circle' : 'fa-times-circle'} status-icon`}></i>
                    <div className="status-card-title">Garage Compatible</div>
                    <div className="status-large">{status.garageCompatible ? 'YES' : 'NO'}</div>
                    <div className="status-description">
                      {status.garageCompatible
                        ? 'This vehicle can enter and park in the garage.'
                        : 'This vehicle may not fit or be supported by the garage system.'}
                    </div>
                  </div>

                  <div className={`status-card ${
                    status.chargingCompatible
                      ? status.programmed ? 'status-green' : 'status-yellow'
                      : 'status-red'
                  }`}>
                    <i className={`fas ${
                      status.chargingCompatible
                        ? status.programmed ? 'fa-check-circle' : 'fa-exclamation-circle'
                        : 'fa-times-circle'
                    } status-icon`}></i>
                    <div className="status-card-title">Charging Compatible</div>
                    <div className="status-large">
                      {status.chargingCompatible ? 'YES' : 'NO'}
                      {!status.programmed && status.chargingCompatible && ' *'}
                    </div>
                    <div className="status-description">
                      {status.chargingCompatible
                        ? status.programmed
                          ? 'This vehicle is fully set up for automated charging.'
                          : <>
                              *This vehicle type is not yet programmed in our system.
                              <br />
                              <a href="mailto:lumeservice@parkworksus.com" className="status-link">
                                <i className="fas fa-envelope"></i> Email Parkworks to request programming
                              </a>
                            </>
                        : 'This vehicle does not support automated charging with our system.'}
                    </div>
                  </div>
                </div>
              ) : selectedMake && !selectedModel ? (
                <div className="prompt-message">
                  <i className="fas fa-arrow-up"></i>
                  <p>Now select a model to check compatibility.</p>
                </div>
              ) : (
                <div className="prompt-message">
                  <i className="fas fa-search"></i>
                  <p>Select your vehicle make and model above to check compatibility.</p>
                </div>
              )}
            </div>

            <div className="info-section">
              <h3><i className="fas fa-info-circle"></i> How it works</h3>
              <div className="info-cards">
                <div className="info-card">
                  <i className="fas fa-warehouse"></i>
                  <h4>Garage Compatible</h4>
                  <p>Confirms your vehicle can physically enter and be parked by our automated garage system.</p>
                </div>
                <div className="info-card">
                  <i className="fas fa-bolt"></i>
                  <h4>Charging Compatible</h4>
                  <p>Confirms your EV can be automatically charged while parked in the garage.</p>
                </div>
                <div className="info-card">
                  <i className="fas fa-cogs"></i>
                  <h4>Programmed</h4>
                  <p>Indicates if your specific vehicle type has been configured in our system for full automation.</p>
                </div>
              </div>
            </div>
          </>
        )}
      </main>

      <footer className="footer">
        <div className="footer-inner">
          <p>&copy; {new Date().getFullYear()} Parkworks. All Rights Reserved.</p>
          <p>
            <a href="mailto:support@parkworksus.com">
              <i className="fas fa-envelope"></i> support@parkworksus.com
            </a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export default App;
